import type { ApiPaginatedResponse, ApiResponse, ApiResult } from '@bcube/shared';
import { api } from '@/lib/api';

export interface ISector {
  id: string;
  name: string;
  enabled: boolean;
  created_at: string;
}

export interface FindManySectorsParams {
  page?: number;
  limit?: number;
  search?: string;
  enabled?: boolean;
}

export interface PaginatedResponse<T> {
  docs: T[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

export const sectorApi = {
  findMany: async (params?: FindManySectorsParams): Promise<PaginatedResponse<ISector>> => {
    const queryParams: Record<string, unknown> = {};

    if (params?.page !== undefined) queryParams.page = params.page;
    if (params?.limit !== undefined) queryParams.limit = params.limit;
    if (params?.search) queryParams.search = params.search;
    if (params?.enabled !== undefined) queryParams.enabled = params.enabled;

    const { data } = await api.get<ApiPaginatedResponse<ISector>>('/commercial/sectors', {
      params: queryParams,
    });
    return {
      docs: data.data,
      total: data.meta.total,
      page: data.meta.page,
      limit: data.meta.limit,
      totalPages: data.meta.pages,
    };
  },

  create: async (name: string): Promise<ApiResult<{ id: string }>> => {
    try {
      const { data } = await api.post<ApiResponse<{ id: string }>>('/commercial/sectors', {
        name,
      });
      return { success: true, data: data.data };
    } catch (error: unknown) {
      return {
        success: false,
        error: {
          global: (error as any)?.response?.data?.error || 'Erro ao criar setor',
          fields: (error as any)?.response?.data?.field
            ? { [(error as any).response.data.field]: (error as any).response.data.error }
            : undefined,
        },
      };
    }
  },

  update: async (id: string, payload: { name?: string; enabled?: boolean }): Promise<ApiResult> => {
    try {
      await api.patch(`/commercial/sectors/${id}`, payload);
      return { success: true };
    } catch (error: unknown) {
      return {
        success: false,
        error: {
          global: (error as any)?.response?.data?.error || 'Erro ao atualizar setor',
        },
      };
    }
  },

  countLinkedAccounts: async (id: string): Promise<number> => {
    const { data } = await api.get<ApiResponse<{ count: number }>>(
      `/commercial/sectors/${id}/linked-accounts-count`,
    );
    return data.data.count;
  },

  delete: async (id: string): Promise<ApiResult> => {
    try {
      await api.delete(`/commercial/sectors/${id}`);
      return { success: true };
    } catch (error: unknown) {
      return {
        success: false,
        error: {
          global: (error as any)?.response?.data?.error || 'Erro ao excluir setor',
        },
      };
    }
  },
};
